import { useEffect, useState } from "react"

import {
    AppError,
    parseAndThrowAppError,
} from "../../../../errors/errorProcessors"
import { getClientProtected } from "../../clients/clients"
import { amendApplicationMapper } from "../../mappers/amendAppication/amendApplicationMapper"
import { ILaunchAmendmentMapped } from "../../types/amendApplication/launchAmendmentTypes"
import { INSTALLER_LAUNCH_AMENDMENT } from "../../urls/hybrid"

interface ILaunchAmendment {
    data: ILaunchAmendmentMapped | undefined
    isLoading: boolean
    error: AppError | null
}

/**
 * Hook launching the single connection application form during an amendment.
 * Queries the launch amend API to collect the form data, and then passes it to
 * the application context. The goal is to replicate the context at the point
 * of submission
 * @returns {ILaunchAmendment} - data, loading and error states.
 */
const useLaunchAmendment = (applicationId: string): ILaunchAmendment => {
    const [data, setData] = useState<ILaunchAmendmentMapped | undefined>(
        undefined
    )
    const [isLoading, setIsLoading] = useState<boolean>(true)
    const [error, setError] = useState<AppError | null>(null)

    useEffect(() => {
        const fetchData = async (): Promise<void> => {
            setIsLoading(true)
            try {
                const url = INSTALLER_LAUNCH_AMENDMENT(applicationId)
                const res = await getClientProtected(url)
                const mappedData = amendApplicationMapper.map(res)
                setData(mappedData)
            } catch (err) {
                setError(parseAndThrowAppError(err))
            } finally {
                setIsLoading(false)
            }
        }

        fetchData()
    }, [applicationId])

    return {
        data,
        isLoading,
        error,
    }
}

export default useLaunchAmendment
